import { Blueprint } from '../../blueprint.class';
import { isExcludedCfn } from './is-excluded.cfn';
import { ToolResult } from '../../types';

export type CurrentDesignAppliance = {
	id: string;
	sku: string;
	category: string;
	name: string;
};

export type CurrentDesignApplianceGroup = {
	groupId: string;
	appliances: Array<CurrentDesignAppliance>;
};

export type CurrentDesignIsland = {
	id: string;
	type: string;
	width: number;
	depth: number;
	groups: Array<CurrentDesignApplianceGroup>;
};

export type CurrentDesignMaterials = {
	countertop: string;
	surround: string;
};

export type CurrentDesign = {
	layout: string;
	description: string;
	islandCount: number;
	islands: Array<CurrentDesignIsland>;
	materials: CurrentDesignMaterials;
};

const CATEGORY_SLUG_MAP: Record<string, string> = {
	'Grills': 'grills',
	'Side Burners': 'side-burners',
	'Fridges': 'fridge',
	'Drop-In Coolers': 'drop-in-coolers',
	'Refreshment Centers': 'refreshment-centers',
	'Pull-Out Ice Chests': 'pull-out-ice-chests',
	'Drop-In Sinks': 'sinks',
	'Farmhouse Sinks': 'farmhouse-sinks',
	'Access Doors': 'access-doors',
	'Combo Door/Drawer Units': 'combo-door-drawer',
	'Storage Drawers': 'storage-drawers',
	'Accessories': 'accessories',
	'Insulated Jacket': 'insulated-jacket',
	'Warming Drawers': 'warming-drawers',
	'Trash': 'trash',
	'Pizza Ovens': 'pizza-ovens',
	'Smokers': 'smokers',
	'Other': 'other',
	'Cutouts': 'cutouts',
};

const normalizeCategory = (description: string): string => {
	if (CATEGORY_SLUG_MAP[description]) return CATEGORY_SLUG_MAP[description];
	return description.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
};

const resolveDepth = (depth: number | { left?: number; right?: number }): number => {
	if (typeof depth === 'number') return depth;
	return Math.max(depth.left ?? 0, depth.right ?? 0);
};

export const getCurrentDesignTool = (blueprint: Blueprint): ToolResult<CurrentDesign> => {
	const kitchen = blueprint.kitchens.getActiveVersion();
	if (!kitchen) return { success: false, error: 'No active kitchen found.' };

	const { design, material } = kitchen;

	const islands: Array<CurrentDesignIsland> = design.islands.children.map((island) => {
		const allGroups = island.panels.children.flatMap((panel) => panel.panelItemGroups.children);
		const sortedGroups = [...allGroups].sort((a, b) => a.center.x - b.center.x);

		const groups: Array<CurrentDesignApplianceGroup> = [];

		for (const group of sortedGroups) {
			const appliances: Array<CurrentDesignAppliance> = group.items.children
				.filter((panelItem) => !isExcludedCfn(panelItem))
				.sort((a, b) => a.center.y - b.center.y)
				.map((panelItem) => ({
					id: panelItem.id,
					sku: panelItem.item.sku,
					category: normalizeCategory(panelItem.item.component.category.description),
					name: panelItem.item.displayText
				}));

			if (appliances.length > 0) {
				groups.push({ groupId: group.id, appliances });
			}
		}

		return {
			id: island.id,
			type: island.type,
			width: island.size.width,
			depth: resolveDepth(island.size.depth as number | { left?: number; right?: number }),
			groups
		};
	});

	const data: CurrentDesign = {
		layout: kitchen.layout,
		description: kitchen.description,
		islandCount: design.islands.length,
		islands,
		materials: {
			countertop: material.countertop.displayName,
			surround: material.surround.displayName
		}
	};

	const applianceCount = islands.reduce(
		(sum, island) => sum + island.groups.reduce((gSum, group) => gSum + group.appliances.length, 0),
		0
	);

	return {
		success: true,
		message: `Design has ${data.islandCount} island(s), ${applianceCount} appliance(s), using ${data.materials.countertop} countertop.`,
		data
	};
};
